import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { Brain } from "lucide-react";
import { supabase } from "../lib/supabase";
import { showToast } from "../lib/toast";
import { useAuth } from "../hooks/useAuth";
import { isNativeShell } from "../lib/native/nativeShell";
import { NATIVE_AUTH_CALLBACK_URL, presentAuthSheet } from "../lib/native/nativeAuth";

export default function LoginPage() {
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const pendingToken = sessionStorage.getItem("pendingInviteToken");
    const pendingAuthorization = sessionStorage.getItem("pendingOAuthAuthorizationId");
    if (pendingAuthorization) {
      // Consent page re-reads (and clears) the stashed id.
      history.push("/oauth/consent");
    } else if (pendingToken) {
      sessionStorage.removeItem("pendingInviteToken");
      history.push(`/invite/${pendingToken}`);
    } else {
      history.push("/");
    }
  }, [user, history]);

  const handleGoogleLogin = async () => {
    try {
      const native = isNativeShell();
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: native ? NATIVE_AUTH_CALLBACK_URL : window.location.origin,
          // Native opens the url in an in-app Safari sheet; the SDK must not navigate the WebView.
          skipBrowserRedirect: native,
          scopes: "openid email profile https://www.googleapis.com/auth/calendar.events.readonly",
          // access_type=offline + prompt=consent are required for Google to
          // reliably issue a `provider_refresh_token`. Without `prompt=consent`,
          // Google only returns a refresh token on the very first consent and
          // omits it on subsequent sign-ins, which would break our calendar
          // refresh path.
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      if (error) {
        console.error("Login error:", error);
        showToast("Sign in failed. Please try again.", "danger");
      } else if (native && data.url) {
        const outcome = await presentAuthSheet({ url: data.url });
        if (outcome === "dismissed") showToast("Sign in was cancelled", "medium");
      }
    } catch (error) {
      console.error("Caught exception during login:", error);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="max-w-sm w-full space-y-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Brain className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Brain Dump
              </h1>
              <p className="text-muted-foreground">
                Dump tasks, let AI organize them
              </p>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center px-4 py-3 border border-border rounded-lg shadow-card bg-surface text-sm font-medium text-foreground hover:shadow-card-hover hover:border-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all active:scale-[0.98]"
            >
              Sign in with Google
            </button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
